import { Component, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { environment } from '@env/environment';
import { ANIMATIONS, COURSE_DISCIPLINES, COURSE_FORMAT, COURSE_LANGUAGE, COURSE_TYPES, LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { ConfirmationDialogueComponent } from 'app/components/application/common/confirmation-dialogue/confirmation-dialogue.component';
import { IPerson } from 'app/models/People/person';
import { IAtp } from 'app/models/atp/atp';
import { IAtpCourse } from 'app/models/atp/atpCourse';
import { IAtpCourseAddress } from 'app/models/atp/atpCourseAddress';
import { IAtpPerson } from 'app/models/atp/atpPerson';
import { IAddress } from 'app/models/common/address';
import { AtpService } from 'app/services/atp/atp.service';
import { CommonDataService } from 'app/services/common/common-data.service';
import { SharedService } from 'app/services/core/shared.service';
import { ToastrService } from 'ngx-toastr';
import { AtpCourseStudentUploadComponent } from '../atp-course-student-upload/atp-course-student-upload.component';
import { AddEditCourseStudentComponent } from '../add-edit-course-student/add-edit-course-student.component';
import { AddEditAtpCourseDocumentComponent } from '../add-edit-atp-course-document/add-edit-atp-course-document.component';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { PaymentService } from 'app/components/payments/services/payments.service';
import { forkJoin } from 'rxjs';

interface CourseAddressOption {
  key: string;
  locationName: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  county: string;
  label: string;
}

@Component({
  selector: 'app-add-edit-atp-course',
  templateUrl: './add-edit-atp-course.component.html',
  styleUrls: ['./add-edit-atp-course.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: ANIMATIONS
})
export class AddEditAtpCourseComponent implements OnInit {
  public personList:IPerson[] = [];
  public filteredPersonList: IPerson[] = []; 
  public courseAddressOptions: CourseAddressOption[] = [];
  public filteredCourseAddressOptions: CourseAddressOption[] = [];

  public environment = environment;
  public uiData = LEAD_BASED_PAINT;
  public courseDataForm = null;
  public courseData: IAtpCourse = null;  

  public courseLocation: IAtpCourseAddress = null;  
  public courseAddress: IAddress = null;  
  
  public tableColumns = ['select', 'id', 'status', 'studentName', 'courseCertificateNumber','testScore','actions'];
  public tableFormat = [        
    {displayName:'Id',columnName:'id', type:'number', size:'10'}, 
    {displayName:'Status',columnName:'status', type:'status', size:'15'},
    {displayName:'Student Name',columnName:'studentName', type:'string', size:'35'},
    {displayName:'Certificate Number',columnName:'courseCertificateNumber', type:'string', size:'20'},
    {displayName:'Test Score',columnName:'testScore', type:'string', size:'10'},
  ];
  public tableData = []
  public tableDataFiltered = []
  public selectedPendingStudents: any[] = [];
  public tablePageSize = 5;
  public filterTableString: string = '';  
  public tableDataSource: MatTableDataSource<any>;
  public changeIndicator: number = 0;
  public cardColor: string = this.sharedService.gold + '20';
  public canEdit: boolean = true;
  @ViewChild('TablePaginator') tablePaginator: MatPaginator;
  @ViewChild('TableSort') tableSort: MatSort;
  @ViewChild(AddEditAtpCourseDocumentComponent) courseDocumentComponent: AddEditAtpCourseDocumentComponent;
  
  constructor(public sharedService: SharedService,
    @Inject(MAT_DIALOG_DATA) public data:{course:IAtpCourse, atp:IAtp, atpStaff:IAtpPerson [], courseId: number},
    private readonly toastr: ToastrService,
    private readonly atpService: AtpService,
    private readonly commonService: CommonDataService,
    public paymentService: PaymentService,    
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.setCourseDataFormFields();
    this.loadData();
  }

  statusColor(status: string): string {
    return this.sharedService.getStatusColor(status);
  }

  togglePendingStudent(row: any, event: any): void {
    const index = this.selectedPendingStudents.findIndex(s => s.id === row.id);
    const isChecked = event.checked || event.source?.checked;
    
    if (isChecked) {
      if (index === -1) {
        this.selectedPendingStudents.push(row);
      }
    } else {
      if (index > -1) {
        this.selectedPendingStudents.splice(index, 1);
      }
    }    
  }

  isPendingStudentSelected(row: any): boolean {
    return this.selectedPendingStudents.some(s => s.id === row.id);
  }

  hasPendingStudents(): boolean {
    return this.tableData.some(row => row.status === 'Pending');
  }

  getPendingStudentCount(): number {
    if (this.selectedPendingStudents.length > 0) {
      return this.selectedPendingStudents.length;
    }
    return this.tableData.filter(row => row.status === 'Pending').length;
  }

  cloneCourse(){
    this.courseData.id = 0;
    this.courseDataForm.controls['courseStart'].setValue(null);
    this.courseDataForm.controls['courseStartTime'].setValue(null);
    this.courseDataForm.controls['courseEnd'].setValue(null);
    this.courseDataForm.controls['courseEndTime'].setValue(null);
    this.courseDataForm.controls['onlineLectureStart'].setValue(null);
    this.courseDataForm.controls['onlineLectureStartTime'].setValue(null);
    this.courseDataForm.controls['onlineLectureEnd'].setValue(null);
    this.courseDataForm.controls['onlineLectureEndTime'].setValue(null);
    this.courseDataForm.controls['cancelled'].setValue(false);
    this.courseDataForm.controls['receivedPreNote'].setValue(false);
    this.courseDataForm.controls['receivedPreNoteDate'].setValue(null);
    this.courseDataForm.controls['receivedPostNote'].setValue(false);
    this.courseDataForm.controls['receivedPostNoteDate'].setValue(null);
    this.courseDataForm.controls['receivedStudentFees'].setValue(false);
    this.courseDataForm.controls['students'].setValue(0);
    this.courseDataForm.controls['controlNumber'].setValue(null);
    this.courseDataForm.controls['overrideDelinquency'].setValue(false);
    this.tableData = [];
    this.tableDataFiltered = [];
    this.courseLocation.id = 0;
    this.courseAddress.id = 0;
  }

  clearAddress(){
    this.courseDataForm.controls['locationName'].setValue(null);
    this.courseDataForm.controls['street'].setValue(null);
    this.courseDataForm.controls['city'].setValue(null);
    this.courseDataForm.controls['state'].setValue(null);
    this.courseDataForm.controls['zip'].setValue(null);
    this.courseDataForm.controls['county'].setValue(null);
  }

  clearInstructor(){
    this.courseDataForm.controls['programInstructor'].setValue(null);
    this.courseDataForm.controls['programInstructorEmail'].setValue(null);
    this.courseDataForm.controls['programInstructorPhone'].setValue(null);
  }

  clearEndDate(){
    this.courseDataForm.controls['courseEnd'].setValue(null);
    this.courseDataForm.controls['courseEndTime'].setValue(null);
  }

  clearStartDate(){    
    this.courseDataForm.controls['courseStart'].setValue(null);
    this.courseDataForm.controls['courseStartTime'].setValue(null);
  }

  clearOnlineLectureEndDate(){
    this.courseDataForm.controls['onlineLectureEnd'].setValue(null);
    this.courseDataForm.controls['onlineLectureEndTime'].setValue(null);
  }

  clearOnlineLectureStartDate(){    
    this.courseDataForm.controls['onlineLectureStart'].setValue(null);
    this.courseDataForm.controls['onlineLectureStartTime'].setValue(null);
  }

  loadData(){
    this.courseLocation = {
      id: 0,
      atpCourse: null,
      address: null,
      locationName: "",
      isCurrentMailing: false,
      isCurrentPhysical: true,
    }

    this.courseAddress = {
      id: 0,
      street: "",
      city: "",
      zip: "",
      contactType: "Work",
      county: "",
      state: "OR",
    }
 
    if ((this.data.courseId !== null && this.data.courseId !== undefined && this.data.courseId !== 0) || this.data.course !== null && this.data.course !== undefined){      
      let id = this.data.courseId || this.data.course.id;
      this.atpService.getATPCourseById(id).subscribe(result=>{        
        this.courseData = result;
        this.loadCourseAddressOptions(this.courseData?.atp?.id || this.data?.atp?.id);
        this.refreshStudentList();
        this.loadInstructors();
        this.loadCourseDataForm();
        this.loadCourseAddress();
      },error=>{
        this.toastr.error("There was an error getting the ATP course: ", error)
      })
    } 
    else{
      this.courseData = {
        id: 0,
        atp: this.data.atp,
        atpCourseAddress: this.data?.course?.atpCourseAddress,
        accreditationDiscipline: null,
        accreditationCourseFormat: null,
        accreditationCourseLanguage: null,
        stage: null,
        programName: null,
        courseStart: null,
        courseEnd: null,
        cancelled: false,
        isPrivate: false,
        receivedAmount: null,
        receivedPreNote: false,
        receivedPreNoteDate: null,
        receivedPostNote: false,
        receivedPostNoteDate: null,
        receivedStudentFees: false,      
        students: 0,
        controlNumber: null,   
        programInstructor: null,
        programInstructorEmail: null,
        programInstructorPhone: null,    
        webAddress: null, 
        overrideDelinquency: false,
        courseFeedback: null,
        onlineLectureStart: null,
        onlineLectureEnd: null
      };
      this.loadCourseAddressOptions(this.courseData?.atp?.id || this.data?.atp?.id);
      this.loadInstructors();
      this.loadCourseDataForm();
      this.loadCourseAddress();
    }    
  }
  
  loadInstructors(){
    this.personList = this.data?.atpStaff?.filter(x=>x.isInstructor || x.isPrimaryInstructor || x.isTrainingManager).map(x => x.person);
    this.filteredPersonList = this.personList;    
  }

  loadCourseDataForm(){
    for (const controlName in this.courseData) {
      if (this.courseDataForm.controls.hasOwnProperty(controlName)) {        
        this.courseDataForm.controls[controlName].setValue(this.courseData[controlName]);          
      }
    }    

    if(this.courseData.courseStart !== null){
      this.courseDataForm.controls['courseStartTime'].setValue(this.courseData.courseStart.split('T')[1]);
    }   
    if(this.courseData.courseEnd !== null){
      this.courseDataForm.controls['courseEndTime'].setValue(this.courseData.courseEnd.split('T')[1]);
    }
    if(this.courseData.onlineLectureStart !== null){
      this.courseDataForm.controls['onlineLectureStartTime'].setValue(this.courseData.onlineLectureStart.split('T')[1]);
    }   
    if(this.courseData.onlineLectureEnd !== null){
      this.courseDataForm.controls['onlineLectureEndTime'].setValue(this.courseData.onlineLectureEnd.split('T')[1]);
    }    
  }

  loadCourseAddress(){
    if(this.courseData !== null && this.courseData.id !== 0){
      this.atpService.getATPCourseAddresses(this.courseData.id).subscribe(result=>{        
        if(result.length){          
          this.courseLocation = result[0];
          this.courseAddress = this.courseLocation.address

          for (const controlName in this.courseAddress) {
            if (this.courseDataForm.controls.hasOwnProperty(controlName)) {        
              this.courseDataForm.controls[controlName].setValue(this.courseAddress[controlName]);          
            }
          }    

          this.courseDataForm.controls['locationName'].setValue(this.courseLocation.locationName);
        }
      },error=>{
        this.toastr.error("There was an error getting the ATP course address: ", error)
      })
    }    
  }

  private getCourseAddressOptionKey(locationName: string, street: string, city: string, state: string, zip: string, county: string): string {
    return [locationName, street, city, state, zip, county]
      .map(value => (value || '').toLowerCase())
      .join('|');
  }

  private buildCourseAddressOptionLabel(locationName: string, street: string, city: string, state: string, zip: string, county: string): string {
    const locality = [city, state].filter(value => !!value).join(', ');
    const localityLine = [locality, zip].filter(value => !!value).join(' ');
    const addressLine = [street, localityLine].filter(value => !!value).join(' | ');
    const countyLine = county ? `${county} County` : '';
    return [locationName, addressLine, countyLine].filter(value => !!value).join(' - ');
  }

  private mapCourseAddressOptions(addresses: IAtpCourseAddress[]): CourseAddressOption[] {
    const dedupedOptions = new Map<string, CourseAddressOption>();

    (addresses || []).forEach(addressRow => {
      const address = addressRow?.address;
      if (!address) {
        return;
      }

      const locationName = (addressRow.locationName || '').toString().trim();
      const street = (address.street || '').toString().trim();
      const city = (address.city || '').toString().trim();
      const state = (address.state || '').toString().trim();
      const zip = (address.zip || '').toString().trim();
      const county = (address.county || '').toString().trim();

      if (!locationName && !street && !city && !state && !zip && !county) {
        return;
      }

      const key = this.getCourseAddressOptionKey(locationName, street, city, state, zip, county);
      if (dedupedOptions.has(key)) {
        return;
      }

      dedupedOptions.set(key, {
        key,
        locationName,
        street,
        city,
        state,
        zip,
        county,
        label: this.buildCourseAddressOptionLabel(locationName, street, city, state, zip, county)
      });
    });

    return Array.from(dedupedOptions.values())
      .sort((left, right) => left.label.localeCompare(right.label));
  }

  private loadCourseAddressOptions(atpId: number | null | undefined): void {
    if (!atpId) {
      this.courseAddressOptions = [];
      this.filteredCourseAddressOptions = [];
      return;
    }

    this.atpService.getATPCourseAddressesByAtpId(atpId).subscribe({
      next: (result) => {
        this.courseAddressOptions = this.mapCourseAddressOptions(result || []);
        this.filteredCourseAddressOptions = [...this.courseAddressOptions];
      },
      error: (error) => {
        console.error('There was an error loading ATP course address options:', error);
        this.courseAddressOptions = [];
        this.filteredCourseAddressOptions = [];
      }
    });
  }

  private filterCourseAddressOptions(searchValue: string): void {
    const normalizedSearchValue = (searchValue || '').toString().toLowerCase().trim();
    if (!normalizedSearchValue) {
      this.filteredCourseAddressOptions = [...this.courseAddressOptions];
      return;
    }

    this.filteredCourseAddressOptions = this.courseAddressOptions.filter(option => {
      const searchable = `${option.locationName} ${option.street} ${option.city} ${option.state} ${option.zip} ${option.county}`.toLowerCase();
      return searchable.includes(normalizedSearchValue);
    });
  }

  private openCourseAddressPanel(trigger: MatAutocompleteTrigger | null | undefined): void {
    if (!trigger) {
      return;
    }

    setTimeout(() => {
      if (!trigger.panelOpen && this.filteredCourseAddressOptions.length > 0) {
        trigger.openPanel();
      }
    });
  }

  onCourseAddressStreetInput(event: any): void {
    this.filterCourseAddressOptions(event?.target?.value || '');
  }

  onCourseAddressStreetFocus(event: any, trigger: MatAutocompleteTrigger): void {
    this.filterCourseAddressOptions(event?.target?.value || '');
    this.openCourseAddressPanel(trigger);
  }

  onCourseAddressOptionSelectionChange(event: any, option: CourseAddressOption): void {
    if (!event?.isUserInput || !event?.source?.selected) {
      return;
    }
    this.selectCourseAddressOption(option);
  }

  selectCourseAddressOption(option: CourseAddressOption): void {
    if (!option) {
      return;
    }

    this.courseDataForm.patchValue({
      locationName: option.locationName || null,
      street: option.street || null,
      city: option.city || null,
      state: option.state || null,
      zip: option.zip || null,
      county: option.county || null
    });
  }

  getCourseAddressOptionLabel(option: CourseAddressOption): string {
    return option?.label || '';
  }

  setCourseDataFormFields(){       
    this.courseDataForm = new UntypedFormGroup({
      accreditationDiscipline: new UntypedFormControl(""),
      accreditationCourseFormat: new UntypedFormControl(""),      
      accreditationCourseLanguage: new UntypedFormControl(""),      
      stage: new UntypedFormControl(""),      
      courseStart: new UntypedFormControl(null),      
      courseStartTime: new UntypedFormControl(null),    
      courseEnd: new UntypedFormControl(null),  
      courseEndTime: new UntypedFormControl(null),  
      onlineLectureStart: new UntypedFormControl(null),      
      onlineLectureStartTime: new UntypedFormControl(null),    
      onlineLectureEnd: new UntypedFormControl(null),  
      onlineLectureEndTime: new UntypedFormControl(null),  
      isPrivate: new UntypedFormControl(false),  
      cancelled: new UntypedFormControl({value: false, disabled: true}),  
      receivedPreNote: new UntypedFormControl(false),  
      receivedPreNoteDate: new UntypedFormControl({value: "", disabled: !this.data?.course?.receivedPreNote}, [Validators.required]),  
      receivedPostNote: new UntypedFormControl(false),  
      receivedPostNoteDate: new UntypedFormControl({value: "", disabled: !this.data?.course?.receivedPreNote}, [Validators.required]),  
      receivedStudentFees: new UntypedFormControl({value: false, disabled: true}),        
      students: new UntypedFormControl({value: "", disabled: true}),  
      controlNumber: new UntypedFormControl({value: "", disabled: true}),        
      locationName: new UntypedFormControl(""), 
      street: new UntypedFormControl(""),
      city: new UntypedFormControl(""),      
      zip: new UntypedFormControl(""),      
      county: new UntypedFormControl(""),      
      state: new UntypedFormControl("OR"),        
      programInstructor: new UntypedFormControl(""), 
      programInstructorEmail: new UntypedFormControl(""), 
      programInstructorPhone: new UntypedFormControl(""), 
      webAddress: new UntypedFormControl(""),          
      overrideDelinquency: new UntypedFormControl(false),   
      courseFeedback: new UntypedFormControl(""),      
    }); 
  }

  updatePreNoteDateDisabledStatus(event: any){
    const receivedPreNoteDateControl = this.courseDataForm.get('receivedPreNoteDate');

    if (event.checked) {      
      receivedPreNoteDateControl.enable();
      receivedPreNoteDateControl.setValue(new Date().toISOString());
    } else {      
      receivedPreNoteDateControl.disable();
      receivedPreNoteDateControl.setValue(null);
    }
  }

  updatePostNoteDateDisabledStatus(event: any){
    const receivedPostNoteDateControl = this.courseDataForm.get('receivedPostNoteDate');

    if (event.checked) {      
      receivedPostNoteDateControl.enable();
      receivedPostNoteDateControl.setValue(new Date().toISOString());
    } else {      
      receivedPostNoteDateControl.disable();
      receivedPostNoteDateControl.setValue(null);
    }
  }

  updateCourseData(): void {
    let timeExpression = /^([01]\d|2[0-3]):([0-5]\d)(:([0-5]\d))?$/;
    for (const controlName in this.courseData) {
      if (this.courseDataForm.controls.hasOwnProperty(controlName)) {
        this.courseData[controlName] = this.courseDataForm.controls[controlName].value;        
      }
    } 

    if(this.courseDataForm.controls['courseStart'].value !== null && this.courseDataForm.controls['courseStartTime'].value !== null && timeExpression.test(this.courseDataForm.controls['courseStartTime'].value)){            
      this.courseData.courseStart = typeof this.courseDataForm.controls['courseStart'].value === 'string' ? `${this.courseDataForm.controls['courseStart'].value.split('T')[0]}T${this.courseDataForm.controls['courseStartTime'].value}` : `${this.courseDataForm.controls['courseStart'].value.toISOString().split('T')[0]}T${this.courseDataForm.controls['courseStartTime'].value}`;
    }

    if(this.courseDataForm.controls['courseEnd'].value !== null && this.courseDataForm.controls['courseEndTime'].value !== null && timeExpression.test(this.courseDataForm.controls['courseEndTime'].value)){
      this.courseData.courseEnd = typeof this.courseDataForm.controls['courseEnd'].value === 'string' ? `${this.courseDataForm.controls['courseEnd'].value.split('T')[0]}T${this.courseDataForm.controls['courseEndTime'].value}` : `${this.courseDataForm.controls['courseEnd'].value.toISOString().split('T')[0]}T${this.courseDataForm.controls['courseEndTime'].value}`;
    }

    if(this.courseDataForm.controls['onlineLectureStart'].value !== null && this.courseDataForm.controls['onlineLectureStartTime'].value !== null && timeExpression.test(this.courseDataForm.controls['onlineLectureStartTime'].value)){            
      this.courseData.onlineLectureStart = typeof this.courseDataForm.controls['onlineLectureStart'].value === 'string' ? `${this.courseDataForm.controls['onlineLectureStart'].value.split('T')[0]}T${this.courseDataForm.controls['onlineLectureStartTime'].value}` : `${this.courseDataForm.controls['onlineLectureStart'].value.toISOString().split('T')[0]}T${this.courseDataForm.controls['onlineLectureStartTime'].value}`;
    }

    if(this.courseDataForm.controls['onlineLectureEnd'].value !== null && this.courseDataForm.controls['onlineLectureEndTime'].value !== null && timeExpression.test(this.courseDataForm.controls['onlineLectureEndTime'].value)){
      this.courseData.onlineLectureEnd = typeof this.courseDataForm.controls['onlineLectureEnd'].value === 'string' ? `${this.courseDataForm.controls['onlineLectureEnd'].value.split('T')[0]}T${this.courseDataForm.controls['onlineLectureEndTime'].value}` : `${this.courseDataForm.controls['onlineLectureEnd'].value.toISOString().split('T')[0]}T${this.courseDataForm.controls['onlineLectureEndTime'].value}`;
    }
    
    for (const controlName in this.courseAddress) {
      if (this.courseDataForm.controls.hasOwnProperty(controlName)) {
        this.courseAddress[controlName] = this.courseDataForm.controls[controlName].value;        
      }
    }   
    
    this.courseLocation.locationName = this.courseDataForm.controls['locationName'].value;
  }

  getCourseDisciplines(){
    return Object.values(COURSE_DISCIPLINES);
  }

  getCourseTypes(){
    return Object.values(COURSE_TYPES);
  }

  getCourseFormats(){
    return Object.values(COURSE_FORMAT);
  }

  getCourseLanguages(){
    return Object.values(COURSE_LANGUAGE);
  }

  // Check if Hybrid format is selected
  isHybridFormat(): boolean {
    const format = this.courseDataForm?.get('accreditationCourseFormat')?.value;
    return format === COURSE_FORMAT.hybrid;
  }

  filterPeople(event: any) {    
    const value = event.target.value;
    if (!value) {
      this.filteredPersonList = [...this.personList];
      return;
    }
    const filterValue = value.toLowerCase();
    this.filteredPersonList = this.personList.filter(person => person.lastName.toLowerCase().includes(filterValue) || person.firstName.toLowerCase().includes(filterValue) || person.id.toString().toLocaleLowerCase().includes(filterValue));    
  } 

  savePrompt(){
    let isNew = this.courseData.id === 0;
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',      
      data: {message: isNew ? 'Are you sure you want to <strong>Add</strong> this Course?' : 'Are you sure you want to <strong>Save</strong> the Course?',title: isNew ? 'Add the Course' : 'Save the Course'},});
    dialogRef.afterClosed().subscribe(result => {
      if(result){
        this.saveData();
      }
    })
  }

  saveData() {
    this.updateCourseData();      
    this.atpService.saveATPCourse(this.courseData).subscribe(result=>{
      this.courseData = result;
      
      this.courseLocation.atpCourse = result;

      this.commonService.saveAddress(this.courseAddress).subscribe(result=>{
        this.courseAddress = result;
        this.courseLocation.address = result

        this.atpService.saveATPCourseAddress(this.courseLocation).subscribe(result=>{
          this.courseLocation = result;
          this.courseAddress = this.courseLocation.address;
          this.changeIndicator++;
          this.toastr.success("Success")          
        },error=>{
          this.toastr.error("There was an error saving the ATP course address: ", error)  
        });
      },error=>{
        this.toastr.error("There was an error saving the address: ", error)  
      })      
    },error=>{
      this.toastr.error("There was an error saving the ATP course: ", error)
    });    
  }

  cancelCourse(cancel: boolean){    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',      
      data: {message: cancel ? 'Are you sure you want to <strong>cancel</strong> the Course?' : 'Are you sure you want to <strong>restore</strong> the Course?',title: cancel ? 'Cancel the Course' : 'Restore the Course'},});
    dialogRef.afterClosed().subscribe(result => {
      if(result){
        this.courseDataForm.controls['cancelled'].setValue(!this.courseDataForm.controls['cancelled'].value)
        this.saveData();
      }
    })
  }

  validateCourseRequiredFields(): boolean {
    const missingFields = [];
    if (!this.courseData.courseStart) missingFields.push('Course Start');
    if (!this.courseData.stage) missingFields.push('Course Type');
    if (!this.courseData.accreditationDiscipline) missingFields.push('Course Discipline');
    if (missingFields.length > 0) {
      if (this.courseDataForm && this.courseDataForm.dirty) {
        this.toastr.error('Please save the course before proceeding. Unsaved changes detected for: ' + missingFields.join(', '), 'Save Required');
      } else {
        this.toastr.error('Please provide: ' + missingFields.join(', '), 'Missing Required Fields');
      }
      return false;
    }
    return true;
  }

  uploadRoster(){
    if (!this.validateCourseRequiredFields()) {
      return;
    }
    const dialogRef = this.dialog.open(AtpCourseStudentUploadComponent, { 
      data:{course:this.courseData},             
      width: '70%',
      autoFocus: false,});
    dialogRef.afterClosed().subscribe(result => {         
      this.refreshStudentList();
      // Refresh documents tab to show the uploaded roster file
      if (this.courseDocumentComponent) {
        this.courseDocumentComponent.loadDocuments();
      }
    });
  }

  processPendingStudents(): void {
    // Get all pending students from table
    const allPendingStudents = this.tableData.filter(row => row.status === 'Pending');
    
    // Use selected students if any, otherwise use all pending students
    let pendingToProcess = this.selectedPendingStudents.length > 0 
      ? [...this.selectedPendingStudents]
      : allPendingStudents;
    
    if (pendingToProcess.length === 0) {
      this.toastr.warning('No pending students to process');
      return;
    }

    const dialogRef = this.dialog.open(AtpCourseStudentUploadComponent, { 
      data: {
        course: this.courseData,
        pendingStudents: pendingToProcess,
        isPendingProcessing: true
      },             
      width: '70%',
      autoFocus: false,});
    dialogRef.afterClosed().subscribe(result => {   
      this.selectedPendingStudents = [];
      this.refreshStudentList();           
    });
  }

  filterTable(): void {    
    this.tableDataFiltered = this.tableData;

    this.tableDataFiltered = this.tableDataFiltered.filter(x => {      
      return x.id?.toString().includes(this.filterTableString) ||      
        x.studentName?.toString().toLowerCase().includes(this.filterTableString.toLowerCase()) ||
        x.courseCertificateNumber?.toString().toLowerCase().includes(this.filterTableString.toLowerCase()) ||
        x.testScore?.toString().toLowerCase().includes(this.filterTableString.toLowerCase())
    });

    this.updateTableDataSource();
  }
  updateTableDataSource(){    
    this.tableDataSource = new MatTableDataSource<any>(this.tableDataFiltered);
    this.tableDataSource.paginator = this.tablePaginator;
    this.tableDataSource.sort = this.tableSort;    
  }

  editTableEntry(row: any): void {
    if (!this.validateCourseRequiredFields()) {
      return;
    }
    const dialogRef = this.dialog.open(AddEditCourseStudentComponent, { 
      data:{student: row,course: this.courseData},             
      width: '70%',
      autoFocus: false,});
    dialogRef.afterClosed().subscribe(result => {          
      this.refreshStudentList();     
    });
  }

  displayLimit(text: any, limit: string){
    let newText = text;
    if (typeof text === 'string' && text.length > parseInt(limit)){
      newText = newText.substring(0, limit) + "...";
    }    
    return newText;
  }

  deleteStudent(row: any){    
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',      
      data: {message: 'Are you sure you want to <strong>Delete</strong> the student?',title: 'Delete the Student'},});
    dialogRef.afterClosed().subscribe(result => {
      if(result){
        const deleteObservable = row.status === 'Pending' 
          ? this.atpService.deleteATPCoursePendingStudent(row.id)
          : this.atpService.deleteATPCourseStudent(row.id);
        
        deleteObservable.subscribe(result=>{
          this.refreshStudentList();          
        },error=>{
          this.toastr.error("There was an error deleting the course student: ", error);
        })  
      }
    })
  }

  refreshStudentList(){
    this.changeIndicator++;
    this.selectedPendingStudents = [];
    forkJoin({
      processedStudents: this.atpService.getATPCourseStudents(this.courseData.id),
      pendingStudents: this.atpService.getATPCoursePendingStudents(this.courseData.id)
    }).subscribe(results => {
      const processedData = results.processedStudents.map((data) => ({
        ...data,
        studentName: data.student.firstName + ' ' + data.student.lastName,
        status: 'Processed'
      }));
      
      const pendingData = results.pendingStudents.map((data: any) => ({
        ...data,
        studentName: data.firstName + ' ' + data.lastName,
        status: 'Pending'
      }));
      
      this.tableData = [...processedData, ...pendingData];
      this.courseDataForm.controls['students'].setValue(this.tableData.length);
      this.filterTable();
    }, error => {
      this.toastr.error("There was an error getting the course students: ", error);
    });
  }
}
